import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'components/Button';
import ConnectButton from 'components/ConnectButton';
import { SelectInput, TextInput } from 'components/Form';
import { FormContainer } from 'components/Form/FormField.styles';
import Row from 'components/Row';
import Stack from 'components/Stack';
import { events } from 'constants/analytics';
import { useWeb3Onboard } from 'hooks';
import { useAnalytics } from 'hooks/useAnalytics';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { generateSignInKeyAPI } from 'screens/login/Login.queries';
import { useTheme } from 'styled-components';
import { showError } from 'utils/toast';
import { useSignupMutation } from './Signup.queries';
import { roleOptions } from './Signup.utils';
import schema from './SignupForm.schema';

type Option = {
	label: string;
	value: number;
};

type SignupFormData = {
	name: string;
	email?: string;
	role?: Option;
	referralCode?: string;
};

const SignupForm = () => {
	const [loading, setLoading] = useState(false);
	const { provider, account } = useWeb3Onboard();
	const { spacings } = useTheme();
	const router = useRouter();
	const { track } = useAnalytics();

	const { mutate: signUp, isLoading: isSigningUp } = useSignupMutation();

	const signMessage = async () => {
		if (!account || !provider) return;

		let signature, signinKey;
		try {
			const { data } = await generateSignInKeyAPI(account);
			signinKey = data.data;

			signature = await provider.getSigner(account).signMessage(signinKey);
		} catch (error) {
			console.error(error);
			showError('Wallet signature denied');
		}

		return { signature, signinKey };
	};

	const {
		register,
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<SignupFormData>({
		defaultValues: { name: '', email: '', role: undefined },
		resolver: yupResolver(schema),
	});

	const onSubmit: SubmitHandler<SignupFormData> = async (values) => {
		setLoading(true);
		const { name, email, role } = values;
		const { referral_code: referralCode } = router.query;
		track(events.user.signup, {
			name,
			email,
			title: role?.label,
			walletAddress: account,
			referralCode,
		});

		const signResponse = await signMessage();

		if (signResponse?.signature && signResponse?.signinKey) {
			const { signature, signinKey } = signResponse;
			signUp({
				walletAddress: account ?? '',
				signature: signature ?? '',
				signinKey: signinKey ?? '',
				name,
				email,
				title: role?.label ?? '',
				funnel: 'ORGANIC',
				referralCode,
			});
		}

		setLoading(false);
	};

	const signupLoading = loading || isSigningUp;
	return (
		<FormContainer onSubmit={handleSubmit(onSubmit)}>
			<Stack gutter={spacings[60]}>
				<Stack gutter={spacings[30]}>
					<TextInput
						label='Name'
						placeholder='John Doe'
						{...register('name')}
						error={errors.name?.message}
					/>
					<TextInput
						label='Email'
						placeholder='john.doe@pudgypenguins.com'
						suggestion='Optional'
						hint='Please enter your email if you wish to enable email notifications.'
						{...register('email')}
						error={errors.email?.message}
					/>
					<Controller
						name='role'
						control={control}
						render={({ field }) => (
							<SelectInput
								size='lg'
								label="What's your job title?"
								options={roleOptions}
								placeholder='Select your role'
								suggestion='Optional'
								maxMenuHeight={200}
								{...field}
							/>
						)}
					/>
				</Stack>
				{/* <Row
					rowGap={0}
					display='inline-block'
					style={{ marginTop: spacings[10] }}
				>
					<Typography variant='span' color='textSecondary' size='xs'>
						By creating an account, you agree to our
					</Typography>
					<ExternalLink href='https://google.com'>
						<Typography
							variant='span'
							size='xs'
							style={{
								padding: `0 ${spacings[4]}`,
								textDecoration: 'underline',
							}}
						>
							Terms
						</Typography>
					</ExternalLink>
					<Typography variant='span' color='textSecondary' size='xs'>
						and have read and acknowledge the
					</Typography>
					<ExternalLink href='https://google.com' target='_blank'>
						<Typography
							variant='span'
							size='xs'
							style={{
								padding: `0 ${spacings[4]}`,
								textDecoration: 'underline',
							}}
						>
							Privacy Policy
						</Typography>
					</ExternalLink>
				</Row> */}
				<Row justifyContent='center'>
					{account ? (
						<Button
							color='primary'
							type='submit'
							style={{ justifySelf: 'center' }}
							isLoading={signupLoading}
							disabled={signupLoading}
						>
							Sign Up
						</Button>
					) : (
						<ConnectButton style={{ justifySelf: 'center' }} />
					)}
				</Row>
			</Stack>
		</FormContainer>
	);
};

export default SignupForm;
